import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Table } from "antd";

import css from "./OrdersDishesListComponent.module.css";
import { ordersActions } from "../../../store/slices/orders-slices";
import { ImageReusable } from "../../../ui";
import { useInitCalendarDate } from "../../../helpers";

const OrdersDishesListComponent = ({ setIsOpen, onDishClick }) => {
  useInitCalendarDate(); //гарантує, що dataOfDay встановиться до рендеру

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { dataOfDay } = useSelector((store) => store.availableDishes);
  const { dishesOrderByDate, trigger } = useSelector((store) => store.orders);

  // Sort dishes by name in the current language
  const sortedDishes = useMemo(() => {
    if (!dishesOrderByDate || dishesOrderByDate.length === 0) {
      return [];
    }

    const currentLanguage = i18n.language;

    return [...dishesOrderByDate].sort((a, b) => {
      const nameA = a.dishName?.[currentLanguage] || a.dishName?.["en"] || "";
      const nameB = b.dishName?.[currentLanguage] || b.dishName?.["en"] || "";

      return nameA.localeCompare(nameB, currentLanguage, {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [dishesOrderByDate, i18n.language]);

  useEffect(() => {
    if (dataOfDay) {
      dispatch(ordersActions.getDishesOrderByDate({ date: dataOfDay }));
    }
  }, [dataOfDay, trigger, dispatch]);

  const columns = useMemo(() => {
    const currentLanguage = i18n.language;

    return [
      {
        title: t("Image"),
        dataIndex: "img",
        key: "img",
        width: "20%",
        render: (img) => (
          <div className={css.imageCell}>
            <ImageReusable
              src={img}
              className={css.imageTag}
              alt="Food Image"
            />
          </div>
        ),
      },
      {
        title: t("modal-name-input-new-category"),
        dataIndex: "dishName",
        key: "dishName",
        width: "55%",
        render: (dishName, record) => {
          const displayName = dishName?.[currentLanguage] || "";
          const excluded = record.excluded || [];

          return (
            <div className={css.nameWrapper}>
              <div className={css.dishName}>{displayName}</div>
              {excluded.length > 0 && (
                <div className={css.excludedText}>
                  Excluded:&nbsp;
                  {excluded.map((item, index) => (
                    <span key={item?.id || `${index}`}>
                      {item?.[currentLanguage]}
                      {index !== excluded.length - 1 && ", "}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: t("weight-netto"),
        dataIndex: "weightNetto",
        key: "weightNetto",
        width: "15%",
        align: "center",
        render: (weightNetto) => weightNetto || 0,
      },
      {
        title: t("Amount"),
        dataIndex: "count",
        key: "count",
        width: "10%",
        align: "center",
      },
    ];
  }, [t, i18n.language]);

  const subDishColumns = useMemo(() => {
    const currentLanguage = i18n.language;

    return [
      {
        title: t("Image"),
        dataIndex: "img",
        key: "img",
        width: "20%",
        render: (img) => (
          <div className={css.imageCell}>
            <ImageReusable
              src={img}
              className={css.subDishImageTag}
              alt="Food Image"
            />
          </div>
        ),
      },
      {
        title: t("modal-name-input-new-category"),
        dataIndex: "dishName",
        key: "dishName",
        width: "55%",
        render: (dishName, record) => {
          const displayName = dishName?.[currentLanguage] || "";
          return (
            <div className={css.nameWrapper}>
              <div className={css.subDishName}>
                <span className={css.subDishBadge}>{record.type}</span>
                {displayName}
              </div>
            </div>
          );
        },
      },
      {
        title: t("weight-netto"),
        dataIndex: "weightNetto",
        key: "weightNetto",
        width: "15%",
        align: "center",
        render: (weightNetto) => weightNetto || 0,
      },
      {
        title: t("Amount"),
        dataIndex: "count",
        key: "count",
        width: "10%",
        align: "center",
        render: () => "-",
      },
    ];
  }, [t, i18n.language]);

  const defaultExpandedRowKeys = useMemo(
    () =>
      sortedDishes
        .map((record, index) =>
          record.subDishes && record.subDishes.length > 0
            ? `dish-${index}`
            : null
        )
        .filter(Boolean),
    [sortedDishes]
  );

  const expandable = useMemo(
    () => ({
      defaultExpandedRowKeys,
      expandedRowRender: (record) => (
        <Table
          columns={subDishColumns}
          dataSource={record.subDishes}
          rowKey={(sub) => `${sub.type}-${sub.dishId}`}
          pagination={false}
          showHeader={false}
          size="small"
          className={css.subDishesTable}
          onRow={(sub) => ({
            onClick: (e) => {
              e.stopPropagation();
              if (onDishClick) {
                onDishClick(sub.dishId);
              }
            },
            style: { cursor: "pointer" },
          })}
        />
      ),
      rowExpandable: (record) =>
        record.subDishes && record.subDishes.length > 0,
    }),
    [subDishColumns, onDishClick, defaultExpandedRowKeys]
  );

  return (
    <div className={css.wrap}>
      <Table
        columns={columns}
        dataSource={sortedDishes}
        rowKey={(record, index) => `dish-${index}`}
        pagination={false}
        locale={{ emptyText: t("no-data") }}
        expandable={expandable}
        scroll={{ x: "max-content" }}
        onRow={(record) => ({
          onClick: () => {
            if (onDishClick) {
              onDishClick(record.dishId);
            }
          },
          style: { cursor: "pointer" },
        })}
        className={css.dishesTable}
      />
    </div>
  );
};

export default React.memo(OrdersDishesListComponent);
